import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { queryClient } from 'src/lib/react-query';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { toast } from 'src/hooks/useToast';
import { ICON_MAP } from 'src/utils/constants';
import { useCreateSubscriptionItemPricingOverride } from 'src/features/subscriptionItemPricingOverrides/api/useCreateSubscriptionItemPricingOverride';
import {
    LIST_SUBSCRIPTION_ITEM_PRICING_OVERRIDES_RESOURCE,
    useListSubscriptionItemPricingOverrides,
} from 'src/features/subscriptionItemPricingOverrides/api/useListSubscriptionItemPricingOverrides';
import { LIST_SUBSCRIPTION_ITEMS_RESOURCE } from 'src/features/subscriptionItems/api/useListSubscriptionItems';
import { pricingOverridesMessages } from 'src/features/subscriptionItemPricingOverrides/utils/messages';
import SubscriptionItemPricingOverrideFormModal from 'src/features/subscriptionItemPricingOverrides/components/forms/SubscriptionItemPricingOverrideFormModal';

function CreateSubscriptionItemPricingOverrideFormModal<TData>({ className }: DataTableNewEntryProps<TData>) {
    const { formatMessage } = useIntl();
    const { subscriptionId = '', subscriptionItemId = '' } = useParams();

    const { data: pricingOverrides } = useListSubscriptionItemPricingOverrides({
        subscriptionId,
        itemId: subscriptionItemId,
        config: { enabled: !!subscriptionId && !!subscriptionItemId },
    });
    const hasOpenCoefficient = !!pricingOverrides?.results.some((override) => override.valid_to === null);

    const { mutate, isPending } = useCreateSubscriptionItemPricingOverride({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_SUBSCRIPTION_ITEM_PRICING_OVERRIDES_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [LIST_SUBSCRIPTION_ITEMS_RESOURCE] });
            toast({
                title: formatMessage(pricingOverridesMessages.createPricingOverrideSuccess),
                description: formatMessage(pricingOverridesMessages.createPricingOverrideSuccessDescription),
            });
        },
    });

    return (
        <SubscriptionItemPricingOverrideFormModal
            className={className}
            hasOpenCoefficient={hasOpenCoefficient}
            mutate={(values, onSuccess, onError) =>
                mutate({ subscriptionId, itemId: subscriptionItemId, ...values }, { onSuccess, onError })
            }
            title={formatMessage(generalMessages.createEntry, {
                entry: formatMessage(pricingOverridesMessages.pricingOverride),
            })}
            icon={ICON_MAP.ADD}
            isPending={isPending}
            subtitle={formatMessage(pricingOverridesMessages.addPricingOverrideSubtitle)}
        />
    );
}

export default CreateSubscriptionItemPricingOverrideFormModal;
